import { regions } from '~/utils/region';

const ORIGINLESS_BASE = 'https://placeholder.invalid';
const ORIGINLESS_HOST = 'placeholder.invalid';

export type DestinationFields =
  | { destination_path: string; destination_url?: undefined }
  | { destination_path?: undefined; destination_url: string };

// A link to one of our own pages is recorded as its path and a link off-site as the href it was
// rendered with — exactly one of the two, and which one is the internal / external split. Nothing
// is normalised: an off-host href is where the user left for, verbatim.
export function destinationFields(href: string): DestinationFields {
  if (!isResolvableWithoutAPage(href)) return { destination_path: href };

  let url;

  try {
    url = new URL(href, ORIGINLESS_BASE);
  } catch {
    return { destination_path: href };
  }

  if (!servesOurPages(url.hostname)) return { destination_url: href };

  return { destination_path: `${url.pathname}${url.search}` };
}

export default function destinationPath(href: string): string {
  return destinationFields(href).destination_path ?? href;
}

// Only an absolute path or a full absolute url means the same thing wherever it was clicked from.
// Anything else — `news/x`, `www.site/x`, `https:news/x` — React Router resolves against the route
// that rendered it, which this function cannot see, so any path it built would name a page the
// browser never opens.
function isResolvableWithoutAPage(href: string): boolean {
  return href.startsWith('/') || /^[a-z][a-z0-9+.-]*:\/\//i.test(href);
}

function servesOurPages(hostname: string): boolean {
  if (hostname === ORIGINLESS_HOST) return true;

  if (typeof window !== 'undefined' && hostname === window.location.hostname)
    return true;

  return regions.some(
    ({ domain }) => hostname === domain || hostname === `www.${domain}`
  );
}
